import isEqual from 'react-fast-compare';
import { DeepPartial } from 'react-hook-form';
import {
  ListingGroupAutoPriceSettings,
  ListingPriceCalculationsInEventInput,
  NetProceedsFloorAdjustment,
  UiMoney,
} from 'src/WebApiController';
/**
 * Helper method to convert listing group auto price settings to ListingPriceCalculationsInEventInput
 */
export const getListingPriceCalculationsInEventInputFromGroupSettings = (
  settings: ListingGroupAutoPriceSettings | undefined,
  defaultValues: Readonly<DeepPartial<ListingGroupAutoPriceSettings>>,
  currencyCode: string
): ListingPriceCalculationsInEventInput => {
  const {
    netProceedsFloor,
    netProceedsCeiling,

    compListingExcludeFanInventory,
    compListingExcludeDefects,
    compListingIncludeUnmappedInventory,
    compListingQuantityFilters,
    compListingSelectedSectionSettings,

    undercutMode,
    undercutReferenceListingPosition,
    undercutAbsoluteAmount,
    undercutRelativeAmount,
    undRelAmt,
    undAbsAmt,

    outlierMode,
    outlierStandardDeviations,
    outlierKthLowestLimit,
    outlierKthLowestLimitRelativeSpacing,
    outlierKthLowestLimitAbsoluteSpacing,
  } = settings ?? {};

  const hasUndercutModeOrReferencePositionChanged =
    undercutMode !== defaultValues?.undercutMode ||
    undercutReferenceListingPosition !==
      defaultValues?.undercutReferenceListingPosition;

  return {
    compListingSetting: {
      mode: { hasChanged: false },
      includeExistingSelectedSectionSettings: { hasChanged: false },
      onlyForSelectedSectionsEnabled: { hasChanged: false },
      quantityFilters: {
        value: compListingQuantityFilters,
        hasChanged: !isEqual(
          compListingQuantityFilters,
          defaultValues?.compListingQuantityFilters
        ),
      },
      selectedSectionSettings: {
        value: compListingSelectedSectionSettings,
        hasChanged:
          compListingSelectedSectionSettings !==
          defaultValues?.compListingSelectedSectionSettings,
      },
      excludeFanInventory: {
        value: compListingExcludeFanInventory,
        hasChanged:
          compListingExcludeFanInventory !==
          defaultValues?.compListingExcludeFanInventory,
      },
      excludeDefects: {
        value: compListingExcludeDefects,
        hasChanged:
          compListingExcludeDefects !==
          defaultValues?.compListingExcludeDefects,
      },
      includeUnmappedInventory: {
        value: compListingIncludeUnmappedInventory,
        hasChanged:
          compListingIncludeUnmappedInventory !==
          defaultValues?.compListingIncludeUnmappedInventory,
      },
    },
    underCutSetting: {
      mode: {
        value: undercutMode,
        hasChanged: hasUndercutModeOrReferencePositionChanged,
      },
      referenceListingPosition: {
        value: undercutReferenceListingPosition,
        hasChanged: hasUndercutModeOrReferencePositionChanged,
      },
      underCutAbsoluteAmount: {
        value: {
          amt: undercutAbsoluteAmount,
          currency: currencyCode,
        },
        hasChanged:
          undercutAbsoluteAmount !== defaultValues?.undercutAbsoluteAmount,
      },
      underCutRelativeAmount: {
        value: undercutRelativeAmount,
        hasChanged:
          undercutRelativeAmount !== defaultValues?.undercutRelativeAmount,
      },
    },
    outlierSetting: {
      mode: {
        value: outlierMode,
        hasChanged: outlierMode !== defaultValues?.outlierMode,
      },
      standardDeviations: {
        value: outlierStandardDeviations,
        hasChanged:
          outlierStandardDeviations !==
          defaultValues?.outlierStandardDeviations,
      },
      outlierLimit: {
        value: outlierKthLowestLimit,
        hasChanged:
          outlierKthLowestLimit !== defaultValues?.outlierKthLowestLimit,
      },
      absoluteOutlierSpacing: {
        value: {
          amt: outlierKthLowestLimitAbsoluteSpacing,
          currency: currencyCode,
        },
        hasChanged:
          outlierKthLowestLimitAbsoluteSpacing !==
          defaultValues?.outlierKthLowestLimitAbsoluteSpacing,
      },
      relativeOutlierSpacing: {
        value: outlierKthLowestLimitRelativeSpacing,
        hasChanged:
          outlierKthLowestLimitRelativeSpacing !==
          defaultValues?.outlierKthLowestLimitRelativeSpacing,
      },
    },
    netProceedsFloor: netProceedsFloor
      ? {
          value: {
            absoluteAmount: netProceedsFloor
              ? ({
                  amt: netProceedsFloor,
                  currency: currencyCode,
                } as UiMoney)
              : null,
            relativeAdjustmentType: null,
            relativeAmount: null,
            relativePercentage: null,
          } as NetProceedsFloorAdjustment,
          hasChanged: netProceedsFloor !== defaultValues?.netProceedsFloor,
        }
      : null,
    netProceedsCeiling: netProceedsCeiling
      ? {
          value: netProceedsCeiling
            ? ({
                amt: netProceedsCeiling,
                currency: currencyCode,
              } as UiMoney)
            : null,
          hasChanged: netProceedsCeiling !== defaultValues?.netProceedsCeiling,
        }
      : null,
    rankPremium: {
      hasChanged:
        undRelAmt !== defaultValues?.undRelAmt ||
        undAbsAmt !== defaultValues?.undAbsAmt,
      value: {
        absoluteAmount: undAbsAmt
          ? ({ amt: undAbsAmt, currency: currencyCode } as UiMoney)
          : null,
        relativePercentage: undRelAmt ? undRelAmt : null,
      },
    },
  } as ListingPriceCalculationsInEventInput;
};
